/* Web push — the "Ada just walked into the Water Cooler" popup.
 *
 * Same message the entry email already sends (see notify_connections in the
 * backend); this is a second delivery pipe, and the admin can switch it off
 * without a deploy (wc_settings.push_notifications_enabled).
 *
 * Platform reality worth knowing before debugging this:
 *   - Android/Chrome/Edge/Firefox: works from the browser, no install needed.
 *   - Desktop Safari: works, needs a user gesture for permission.
 *   - iOS/iPadOS: 16.4+ ONLY, and ONLY once the site is on the home screen.
 *     Before that, PushManager does not exist — hence isIosNeedsInstall().
 */

import api from "./api";
import { API_PATHS } from "../config/apiConfig";
import log from "../utils/logger";

const SW_URL = "/service-worker.js";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function isStandalone() {
  return (
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
  );
}

/** iPhone/iPad in Safari: push is real but unreachable until "Add to Home
 *  Screen". Callers show the how-to instead of a dead toggle. */
export function isIosNeedsInstall() {
  const ua = navigator.userAgent || "";
  const ios =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios && !isStandalone() && !("PushManager" in window);
}

/** Registered once at boot. Also what makes the browser offer "Install". */
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL);
  } catch (e) {
    log.warn("service worker registration failed", e);
    return null;
  }
}

/** VAPID keys travel as base64url; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** Server side of the switch: is push on, and what key do we subscribe with?
 *  Rides the public activity payload — no extra API Gateway path. */
export async function fetchPushConfig() {
  try {
    const r = await api.get(API_PATHS.PUBLIC.WATERCOOLER_ACTIVITY);
    return {
      enabled: !!r.data?.push_enabled,
      vapidPublicKey: r.data?.vapid_public_key || "",
    };
  } catch (e) {
    return { enabled: false, vapidPublicKey: "" };
  }
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/** Everything a settings toggle needs to render itself honestly. */
export async function getPushState() {
  const config = await fetchPushConfig();
  if (!pushSupported()) {
    return {
      ...config,
      supported: false,
      needsIosInstall: isIosNeedsInstall(),
      permission: "unsupported",
      subscribed: false,
    };
  }
  let subscribed = false;
  try {
    subscribed = !!(await currentSubscription());
  } catch (e) {
    subscribed = false;
  }
  return {
    ...config,
    supported: true,
    needsIosInstall: false,
    permission: Notification.permission,
    subscribed,
  };
}

/** Must be called from a click — browsers reject permission prompts otherwise. */
export async function enablePush() {
  if (!pushSupported()) throw new Error("unsupported");

  const { enabled, vapidPublicKey } = await fetchPushConfig();
  if (!enabled || !vapidPublicKey) throw new Error("disabled");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(permission); // 'denied' | 'default'

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();

  // A key rotation leaves a subscription the server can no longer encrypt for;
  // drop it and re-subscribe rather than silently going quiet.
  if (sub) {
    const existing = sub.options?.applicationServerKey;
    const wanted = urlBase64ToUint8Array(vapidPublicKey);
    if (existing && !sameKey(new Uint8Array(existing), wanted)) {
      await sub.unsubscribe().catch(() => {});
      sub = null;
    }
  }

  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  await api.post(API_PATHS.WATERCOOLER.PUSH, {
    action: "subscribe",
    subscription: sub.toJSON(),
    user_agent: navigator.userAgent.slice(0, 300),
  });
  return true;
}

function sameKey(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (!sub) return true;
  // Tell the server FIRST: if unsubscribe() succeeds and the POST then fails,
  // the row lingers and every future send burns a request on a dead endpoint.
  await api
    .post(API_PATHS.WATERCOOLER.PUSH, { action: "unsubscribe", endpoint: sub.endpoint })
    .catch((e) => log.warn("push unsubscribe not recorded server-side", e));
  await sub.unsubscribe().catch(() => {});
  return true;
}
